/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { isObject } from 'lodash/fp';
import { useCallback } from 'react';
import { type GiftVoucherModuleEmailContentsSpecsFragment } from '../../../../api/fragments';
import { type UploadFileWithPreviewFormDataFragment } from '../../../../api/fragments/UploadFileWithPreviewFormData';
import {
    type DeleteGiftVoucherModuleAssetMutation,
    type DeleteGiftVoucherModuleAssetMutationVariables,
    DeleteGiftVoucherModuleAssetDocument,
} from '../../../../api/mutations/deleteGiftVoucherModuleAsset';
import {
    type UploadGiftVoucherModuleAssetMutation,
    type UploadGiftVoucherModuleAssetMutationVariables,
    UploadGiftVoucherModuleAssetDocument,
} from '../../../../api/mutations/uploadGiftVoucherModuleAsset';
import { GiftVoucherModuleAsset } from '../../../../api/types';
import { type GiftVoucherEmailFormValues } from './shared';

type UploadParams = {
    moduleId: string;
    upload: File | UploadFileWithPreviewFormDataFragment;
    type: GiftVoucherModuleAsset;
};

const useHandleGiftVoucherModuleAssets = () => {
    const apolloClient = useApolloClient();

    const updateOrDelete = useCallback(
        ({ type, moduleId, upload }: UploadParams, initialValue: UploadFileWithPreviewFormDataFragment) => {
            const uploads = [];

            if (upload instanceof File) {
                // upload a new file
                uploads.push(
                    apolloClient.mutate<
                        UploadGiftVoucherModuleAssetMutation,
                        UploadGiftVoucherModuleAssetMutationVariables
                    >({
                        mutation: UploadGiftVoucherModuleAssetDocument,
                        variables: {
                            moduleId,
                            type,
                            upload,
                        },
                    })
                );
            } else if (upload === null && isObject(initialValue)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<
                        DeleteGiftVoucherModuleAssetMutation,
                        DeleteGiftVoucherModuleAssetMutationVariables
                    >({
                        mutation: DeleteGiftVoucherModuleAssetDocument,
                        variables: { moduleId, type },
                    })
                );
            }

            return uploads;
        },
        [apolloClient]
    );

    return useCallback(
        (
            moduleId: string,
            values: GiftVoucherEmailFormValues,
            initialValues?: GiftVoucherModuleEmailContentsSpecsFragment
        ) => {
            const { customer } = values;

            const uploads = [
                ...updateOrDelete(
                    {
                        moduleId,
                        upload: customer.confirmation.introImage,
                        type: GiftVoucherModuleAsset.CustomerConfirmation,
                    },
                    initialValues?.customer.confirmation.introImage
                ),
            ];

            if (uploads.length) {
                return Promise.all(uploads);
            }

            return Promise.resolve([]);
        },
        [updateOrDelete]
    );
};

export default useHandleGiftVoucherModuleAssets;
